import { useCallback, useRef } from "react";

import type { OpenTask } from "@storyboard/dispatch-view/task";
import { isAnchorAcknowledgementSubject } from "@/models/acknowledgement/acknowledgementStore";
import {
  type AcknowledgementIntent,
  acknowledgementWriteFor,
  recordAcknowledgements,
} from "@/models/acknowledgement/acknowledgementWrites";
import { latestItemForFeed } from "@/store/feedSelectors";
import {
  type AcknowledgementRequirement,
  DEFAULT_REQUIREMENT,
  feedWatermark,
  feedWatermarkThrough,
} from "./acknowledgements";
import type { DispatchItem } from "./dispatchItems";
import { type AcknowledgeGrain, UNDO_LIMIT } from "./dispatchModel";
import type { DispatchTableRow } from "./dispatchTableRow";
import { rowFeedId } from "./dispatchTableRow";
import { type AcknowledgementTrigger, trackAcknowledgement } from "./dispatchTracking";
import { type ItemTarget, itemTargetOf, NO_TASKS_ELSEWHERE, type TasksElsewhere } from "./tasks";

/** Who acknowledges, where, and what the view asks them to: what every write on the page is stamped with. */
export type AcknowledgementScope = {
  workspaceId: string;
  membershipId: string | undefined;
  /** What the view asks its team to acknowledge; a channel's watermark covers through the newest of it. */
  requires?: AcknowledgementRequirement;
  /** The open tasks pinned to other boards, by driver, which a channel's watermark covers when the list takes them. */
  elsewhere?: TasksElsewhere;
  /** The open tasks each shelf row stands for, by row id, which acknowledging the row closes; none off the shelf. */
  shelfTasksByRow: ReadonlyMap<string, readonly OpenTask[]>;
};

/**
 * What a row's own acknowledgement targets at the message grain: the message on a message row, the event on an event
 * row, the task alone on a row of a task pinned to another board, the item a task row is about, and on a driver row
 * the driver's latest message. The driver's, not the
 * channel's: after a reply the newest item is the dispatcher's own, and handling that would clear nothing.
 *
 * @param row - The row acted on.
 * @param feedId - The row's channel, whose latest driver message a driver row targets.
 * @param action - Whether the target is handled or taken back.
 */
export function rowTarget(
  row: DispatchTableRow,
  feedId: string,
  action: AcknowledgementIntent["action"],
): AcknowledgementIntent | null {
  if (row.kind === "message") {
    // A task pinned to another board is acknowledged alone: what it is about stays as its own log leaves it.
    if (row.item.source === "otherBoardTask" && row.item.task) return taskOnlyIntent(row.item.task.task.id, action);
    return itemIntent(row.item, action);
  }
  // A task row's own target is the message or event it is about; a task about a driver names none.
  if (row.kind === "task") return row.item ? itemIntent(row.item, action) : null;
  const latest = latestItemForFeed(feedId, (item) => item.isFromAliasMember === true)?.id;
  return latest ? { subject: "feed_item", action, feedItemId: latest } : null;
}

/** The acknowledgement of one message or event; null for an event of a subject no log takes. */
function itemIntent(item: DispatchItem, action: AcknowledgementIntent["action"]): AcknowledgementIntent | null {
  const target = itemTargetOf(item);
  return target ? targetIntent(target, action) : null;
}

/**
 * The acknowledgement of a named message or event: a message's by its id, an event's by its anchor; null for an event
 * of a subject no log takes.
 */
export function targetIntent(
  target: ItemTarget,
  action: AcknowledgementIntent["action"],
): AcknowledgementIntent | null {
  const { subject, targetId } = target;
  if (subject === "feed_item") return { subject, action, feedItemId: targetId };
  return isAnchorAcknowledgementSubject(subject) ? { subject, action, anchorId: targetId } : null;
}

/**
 * The intents acknowledging tasks from the shelf: each task's, and each message's or event's it is about.
 *
 * One batch, so one undo takes them all back together. A task about a driver acknowledges the task alone.
 *
 * @param tasks - The tasks the acknowledged rows stand for.
 * @param action - Whether they are handled or taken back.
 */
export function taskIntents(
  tasks: readonly OpenTask[],
  action: AcknowledgementIntent["action"],
): AcknowledgementIntent[] {
  return tasks.flatMap(({ task }) => [
    taskOnlyIntent(task.id, action),
    ...task.subjects.flatMap(({ subject, targetId }) =>
      subject === "membership" ? [] : (targetIntent({ subject, targetId }, action) ?? []),
    ),
  ]);
}

/**
 * The intent handling a task alone, or taking its handling back, leaving the messages or events it is about as they
 * stand.
 *
 * What History writes to reopen a done task, which the shelf no longer holds.
 *
 * @param taskId - The task.
 * @param action - Whether it is handled or taken back.
 */
export function taskOnlyIntent(taskId: string, action: AcknowledgementIntent["action"]): AcknowledgementIntent {
  return { subject: "task", action, taskId };
}

/**
 * The inverse of an intent: the same target, the other action. What undo writes.
 *
 * A channel unacknowledge carries the `through` of the watermark it pops, so undoing it restores that watermark
 * exactly rather than covering everything that arrived since.
 */
function inverseOf(intent: AcknowledgementIntent): AcknowledgementIntent {
  const action = intent.action === "acknowledged" ? "unacknowledged" : "acknowledged";
  return { ...intent, action } as AcknowledgementIntent;
}

/**
 * The acknowledgements the page writes and the undo that takes them back.
 *
 * Every action is one row in its subject's acknowledgement log, recorded into the store at once and then to the
 * server (`acknowledgementWrites.ts`); the rows redraw from the store as each one lands. A channel acknowledgement is
 * a watermark through the newest arrival the page holds for it, never fanned out to the channel's items. Every
 * action this session takes leaves its inverse on the undo stack, {@link UNDO_LIMIT} deep, so Ctrl or Cmd with Z
 * takes back the newest. `acknowledgeRow` and `unacknowledgeRow` act at the view's grain: the row's channel on a
 * driver-per-row view, the one message or event on a message-per-row view; the feed pair always acts on the channel.
 * The plural forms take the ticked rows: each at the grain, or each row's channel once however many are ticked. The
 * item and task forms are History's: one message or event by name, and one task with what it is about, or reopened.
 * Every write names what triggered it, and reports one Amplitude event per target ({@link trackAcknowledgement});
 * an undo reports the inverse it writes under the `undo` trigger.
 */
export function useAcknowledgements(
  grain: AcknowledgeGrain,
  scope: AcknowledgementScope,
): {
  acknowledgeRow: (row: DispatchTableRow, feedId: string, trigger: AcknowledgementTrigger) => void;
  unacknowledgeRow: (row: DispatchTableRow, feedId: string, trigger: AcknowledgementTrigger) => void;
  acknowledgeFeed: (feedId: string, trigger: AcknowledgementTrigger) => void;
  unacknowledgeFeed: (feedId: string, trigger: AcknowledgementTrigger) => void;
  acknowledgeRows: (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) => void;
  unacknowledgeRows: (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) => void;
  acknowledgeChannelsOf: (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) => void;
  /** Handles one message or event by name, or takes its handling back; an event no log takes is left alone. */
  setItemAcknowledged: (
    target: ItemTarget,
    action: AcknowledgementIntent["action"],
    trigger: AcknowledgementTrigger,
  ) => void;
  /** Closes an open task and the messages or events it is about in one batch, as acknowledging its shelf row does. */
  acknowledgeTask: (task: OpenTask, trigger: AcknowledgementTrigger) => void;
  /** Reopens a done task: the task's own acknowledgement taken back, the items it is about left as they stand. */
  reopenTask: (taskId: string, trigger: AcknowledgementTrigger) => void;
  undo: () => void;
} {
  const {
    workspaceId,
    membershipId,
    requires = DEFAULT_REQUIREMENT,
    elsewhere = NO_TASKS_ELSEWHERE,
    shelfTasksByRow,
  } = scope;
  const undoRef = useRef<(() => void)[]>([]);

  const record = useCallback(
    (intents: readonly AcknowledgementIntent[], trigger: AcknowledgementTrigger, remember = true) => {
      if (intents.length === 0) return;
      const writer = { workspaceId, actorWorkspaceMembershipId: membershipId ?? null };
      recordAcknowledgements(writer, intents.map(acknowledgementWriteFor));
      for (const intent of intents) trackAcknowledgement(workspaceId, intent, trigger);
      if (!remember) return;
      undoRef.current.push(() => {
        const inverse = intents.map(inverseOf);
        recordAcknowledgements(writer, inverse.map(acknowledgementWriteFor));
        for (const intent of inverse) trackAcknowledgement(workspaceId, intent, "undo");
      });
      if (undoRef.current.length > UNDO_LIMIT) undoRef.current.shift();
    },
    [workspaceId, membershipId],
  );

  const feedIntent = useCallback(
    (feedId: string, action: AcknowledgementIntent["action"]): AcknowledgementIntent => {
      if (action === "unacknowledged") {
        // The server stores no `through` on an unacknowledge; it rides here only for the undo to restore.
        const popped = feedWatermark(feedId)?.through;
        return popped === undefined
          ? { subject: "feed", action, feedId }
          : { subject: "feed", action, feedId, through: popped };
      }
      const through = feedWatermarkThrough(feedId, requires, elsewhere);
      return through === null ? { subject: "feed", action, feedId } : { subject: "feed", action, feedId, through };
    },
    [requires, elsewhere],
  );

  const acknowledgeFeed = useCallback(
    (feedId: string, trigger: AcknowledgementTrigger) => record([feedIntent(feedId, "acknowledged")], trigger),
    [record, feedIntent],
  );
  const unacknowledgeFeed = useCallback(
    (feedId: string, trigger: AcknowledgementTrigger) => record([feedIntent(feedId, "unacknowledged")], trigger),
    [record, feedIntent],
  );

  const intentsFor = useCallback(
    (rows: readonly DispatchTableRow[], action: AcknowledgementIntent["action"]): AcknowledgementIntent[] =>
      rows.flatMap((row) => {
        // A shelf row acts on the tasks it stands for, whatever the grain.
        const shelved = shelfTasksByRow.get(row.id) ?? [];
        if (shelved.length > 0) return taskIntents(shelved, action);
        const feedId = rowFeedId(row);
        if (!feedId) return [];
        if (grain === "channel") return [feedIntent(feedId, action)];
        return rowTarget(row, feedId, action) ?? [];
      }),
    [grain, feedIntent, shelfTasksByRow],
  );

  const acknowledgeRow = useCallback(
    (row: DispatchTableRow, _feedId: string, trigger: AcknowledgementTrigger) =>
      record(intentsFor([row], "acknowledged"), trigger),
    [record, intentsFor],
  );
  const unacknowledgeRow = useCallback(
    (row: DispatchTableRow, _feedId: string, trigger: AcknowledgementTrigger) =>
      record(intentsFor([row], "unacknowledged"), trigger),
    [record, intentsFor],
  );
  const acknowledgeRows = useCallback(
    (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) =>
      record(intentsFor(rows, "acknowledged"), trigger),
    [record, intentsFor],
  );
  const unacknowledgeRows = useCallback(
    (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) =>
      record(intentsFor(rows, "unacknowledged"), trigger),
    [record, intentsFor],
  );
  const acknowledgeChannelsOf = useCallback(
    (rows: readonly DispatchTableRow[], trigger: AcknowledgementTrigger) => {
      const feedIds = [...new Set(rows.map(rowFeedId))].filter((feedId): feedId is string => feedId !== null);
      record(
        feedIds.map((feedId) => feedIntent(feedId, "acknowledged")),
        trigger,
      );
    },
    [record, feedIntent],
  );
  const setItemAcknowledged = useCallback(
    (target: ItemTarget, action: AcknowledgementIntent["action"], trigger: AcknowledgementTrigger) => {
      const intent = targetIntent(target, action);
      if (intent) record([intent], trigger);
    },
    [record],
  );
  const acknowledgeTask = useCallback(
    (task: OpenTask, trigger: AcknowledgementTrigger) => record(taskIntents([task], "acknowledged"), trigger),
    [record],
  );
  const reopenTask = useCallback(
    (taskId: string, trigger: AcknowledgementTrigger) => record([taskOnlyIntent(taskId, "unacknowledged")], trigger),
    [record],
  );
  const undo = useCallback(() => {
    undoRef.current.pop()?.();
  }, []);

  return {
    acknowledgeRow,
    unacknowledgeRow,
    acknowledgeFeed,
    unacknowledgeFeed,
    acknowledgeRows,
    unacknowledgeRows,
    acknowledgeChannelsOf,
    setItemAcknowledged,
    acknowledgeTask,
    reopenTask,
    undo,
  };
}
